import { CloseOutlined } from "@mui/icons-material";
import {
    Box,
    IconButton,
    Popover,
    Stack,
    TextField,
    Typography,
    Divider,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { LoadingButton } from "@mui/lab";
import { useSetPhoneCallMutation } from "hooks/api/useLinqRequestListing";
import { useLinqRequests } from "hooks/useLinqRequests";
import useNotifications from "hooks/useNotifications";
import PhoneCallStepper from "./PhoneCallStepper";
import GeneralUtilityService from "services/GeneralUtilityService";
import { useSelector } from "react-redux";

function nowDateTime() {
    return format(new Date(), "yyyy-MM-dd");
}

export const PhoneCallDoneRow = ({ phoneCalls = [], id }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [nextDate, setNextDate] = useState(nowDateTime());
    const { renderErrorNotification } = useNotifications();
    const { filters, resultLength, page } = useSelector(
        (state) => state.linqRequestsSlice,
    );

    const {
        mutate: setPhoneCall,
        isLoading,
        isSuccess,
        isError,
    } = useSetPhoneCallMutation();

    const isOpen = Boolean(anchorEl);
    const lastCall = phoneCalls[phoneCalls.length - 1] || {};
    const {
        recruiterEmail: latestRecruiterEmail,
        formattedTime: latestFormatted,
    } = GeneralUtilityService.getCallInfo(lastCall);

    const { getLinqRequests } = useLinqRequests();
    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = useCallback(() => {
        setAnchorEl(null);
        setNextDate(nowDateTime());
    }, []);

    const handleDateChange = (e) => setNextDate(e.target.value);

    const handleDateSubmit = async () => {
        if (!nextDate || !id) return;

        const dt = parseISO(nextDate);
        const date = format(dt, "yyyy-MM-dd");
        await setPhoneCall({ id, date });
    };

    useEffect(() => {
        if (isSuccess) {
            getLinqRequests({
                ...filters,
                page,
                page_size: resultLength,
            });
        }

        if (isError) {
            renderErrorNotification(
                `Oops! Something went wrong updating your phone calls. Give it another shot in a minute, and if it keeps failing, our support team is here to help.`,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, isSuccess]);

    if (!id) return null;

    return (
        <>
            <Stack
                onClick={handleOpen}
                justifyContent="center"
                alignItems="flex-start"
                sx={{ width: "100%", height: "100%", p: 1, cursor: "pointer" }}
            >
                <Typography variant="subtitle2" color="black">
                    {latestRecruiterEmail}
                </Typography>
                {Boolean(latestFormatted) && (
                    <Typography variant="caption" color="text.secondary">
                        Last call: {latestFormatted}
                    </Typography>
                )}
            </Stack>

            <Popover
                id={`phone-call-${id}`}
                open={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                transformOrigin={{ vertical: "center", horizontal: "center" }}
            >
                <Box sx={{ width: 320, p: 3, position: "relative" }}>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseOutlined fontSize="small" />
                    </IconButton>

                    <Typography variant="body1" fontWeight={600} gutterBottom>
                        Call History
                    </Typography>

                    <PhoneCallStepper phoneCalls={phoneCalls} />

                    <Box py={3}>
                        <Divider />
                    </Box>

                    <Stack gap={2}>
                        <TextField
                            label="Did you call? Enter Date!"
                            type="date"
                            variant="standard"
                            value={nextDate}
                            onChange={handleDateChange}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />

                        <LoadingButton
                            variant="contained"
                            size="small"
                            onClick={handleDateSubmit}
                            loading={isLoading}
                        >
                            Submit date
                        </LoadingButton>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
};

export default PhoneCallDoneRow;
